/** @format */

import { inject, Injectable } from "@angular/core";
import { plainToInstance } from "class-transformer";
import { map, Observable } from "rxjs";
import { CreateVacancyDto } from "@domain/vacancy/dto/create-vacancy.model";
import {
  SendVacancyResponsePayload,
  VacancyResponse,
} from "@domain/vacancy/vacancy-response.model";
import { Vacancy } from "@domain/vacancy/vacancy.model";
import { VacancyHttpAdapter } from "../../adapters/vacancy/vacancy-http.adapter";
import { VacancyRepositoryPort } from "@domain/vacancy/ports/vacancy.repository.port";
import { EntityCache } from "@domain/shared/entity-cache";
import { EventBus } from "@domain/shared/event-bus";
import { VacancyCreated } from "@domain/vacancy/events/vacancy-created.event";
import { VacancyUpdated } from "@domain/vacancy/events/vacancy-updated.event";
import { VacancyDelete } from "@domain/vacancy/events/vacancy-deleted.event";
import { LoggedOut } from "@domain/auth/events/logged-out.event";

/** Репозиторий вакансий с кешем деталей и инвалидацией по событиям вакансий. */
@Injectable({ providedIn: "root" })
export class VacancyRepository implements VacancyRepositoryPort {
  private readonly vacancyAdapter = inject(VacancyHttpAdapter);
  private readonly eventBus = inject(EventBus);
  private readonly entityCache = new EntityCache<Vacancy>();

  private readonly projectVacanciesCache = new EntityCache<Vacancy[]>();

  constructor() {
    this.eventBus.on<VacancyCreated>("VacancyCreated").subscribe(event => {
      this.projectVacanciesCache.invalidate(event.payload.projectId);
    });

    this.eventBus.on<VacancyUpdated>("VacancyUpdated").subscribe(event => {
      this.entityCache.invalidate(event.payload.vacancyId);
      this.projectVacanciesCache.clear();
    });

    this.eventBus.on<VacancyDelete>("VacancyDelete").subscribe(event => {
      this.entityCache.invalidate(event.payload.vacancyId);
      this.projectVacanciesCache.clear();
    });

    this.eventBus.on<LoggedOut>("LoggedOut").subscribe({
      next: () => {
        this.entityCache.clear();
        this.projectVacanciesCache.clear();
      },
    });
  }

  /** Первая страница проекта без фильтров кешируется по projectId. */
  getForProject(
    limit: number,
    offset: number,
    projectId?: number,
    requiredExperience?: string,
    workFormat?: string,
    workSchedule?: string,
    salary?: string,
    searchValue?: string,
  ): Observable<Vacancy[]> {
    const isDefaultFirstPage =
      projectId !== undefined &&
      limit === 20 &&
      offset === 0 &&
      !requiredExperience &&
      !workFormat &&
      !workSchedule &&
      !salary &&
      !searchValue;

    const fetch$ = this.vacancyAdapter
      .getForProject(
        limit,
        offset,
        projectId,
        requiredExperience,
        workFormat,
        workSchedule,
        salary,
        searchValue,
      )
      .pipe(map(vacancies => plainToInstance(Vacancy, vacancies.results)));

    return isDefaultFirstPage
      ? this.projectVacanciesCache.getOrFetch(projectId!, () => fetch$)
      : fetch$;
  }

  getMyVacancies(limit: number, offset: number): Observable<VacancyResponse[]> {
    return this.vacancyAdapter.getMyVacancies(limit, offset).pipe(
      map(responses => (Array.isArray(responses) ? responses : responses.results)),
      map(responses => plainToInstance(VacancyResponse, responses)),
    );
  }

  getOne(vacancyId: number): Observable<Vacancy> {
    return this.entityCache.getOrFetch(vacancyId, () =>
      this.vacancyAdapter.getOne(vacancyId).pipe(map(vacancy => plainToInstance(Vacancy, vacancy))),
    );
  }

  postVacancy(projectId: number, vacancy: CreateVacancyDto): Observable<Vacancy> {
    return this.vacancyAdapter
      .postVacancy(projectId, vacancy)
      .pipe(map(createdVacancy => plainToInstance(Vacancy, createdVacancy)));
  }

  updateVacancy(
    vacancyId: number,
    vacancy: Partial<Vacancy> | CreateVacancyDto,
  ): Observable<Vacancy> {
    return this.vacancyAdapter
      .updateVacancy(vacancyId, vacancy)
      .pipe(map(updatedVacancy => plainToInstance(Vacancy, updatedVacancy)));
  }

  deleteVacancy(vacancyId: number): Observable<void> {
    this.entityCache.invalidate(vacancyId);
    return this.vacancyAdapter.deleteVacancy(vacancyId);
  }

  sendResponse(vacancyId: number, body: SendVacancyResponsePayload): Observable<VacancyResponse> {
    return this.vacancyAdapter
      .sendResponse(vacancyId, body)
      .pipe(map(response => plainToInstance(VacancyResponse, response)));
  }

  responsesByVacancy(vacancyId: number): Observable<VacancyResponse[]> {
    return this.vacancyAdapter
      .responsesByVacancy(vacancyId)
      .pipe(map(responses => plainToInstance(VacancyResponse, responses)));
  }

  responsesByProject(projectId: number): Observable<VacancyResponse[]> {
    return this.vacancyAdapter
      .responsesByProject(projectId)
      .pipe(map(responses => plainToInstance(VacancyResponse, responses)));
  }

  acceptResponse(responseId: number): Observable<VacancyResponse> {
    return this.vacancyAdapter
      .acceptResponse(responseId)
      .pipe(map(response => plainToInstance(VacancyResponse, response)));
  }

  rejectResponse(responseId: number): Observable<VacancyResponse> {
    return this.vacancyAdapter
      .rejectResponse(responseId)
      .pipe(map(response => plainToInstance(VacancyResponse, response)));
  }
}
